import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, CalendarDays, Clock, User, Loader2 } from "lucide-react";

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function formatFecha(fecha) {
  if (!fecha) return "";
  const [y, m, d] = fecha.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DIAS[dt.getDay()]} ${d} de ${MESES[m - 1]} de ${y}`;
}

const TIPO_LABEL = {
  origen: "Test ORIGEN (evaluación inicial)",
  sesion: "Sesión terapéutica",
  reunion: "Reunión",
  consulta_externa: "Consulta externa",
  otro: "Consulta",
};

export default function ConfirmarTurno() {
  const { token } = useParams();
  const [turno, setTurno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/public/turnos/${token}`);
      setTurno(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [token]);

  const confirmar = async () => {
    setConfirming(true);
    try {
      await api.post(`/public/turnos/${token}/confirmar`);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="grain min-h-screen bg-[hsl(var(--background))] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-mark.png" alt="Consultorio" className="w-20 h-20 object-contain mb-3" />
          <div className="font-bold text-neutral-900 tracking-tight text-lg">{turno?.profesional_nombre || "Consultorio"}</div>
          <div className="overline text-neutral-500">Confirmación de turno</div>
        </div>

        {loading && (
          <div className="card-clinic p-10 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" />
            <div className="mt-3 text-sm text-neutral-500">Cargando tu turno…</div>
          </div>
        )}

        {!loading && error && (
          <div className="card-clinic p-8 text-center">
            <div className="overline text-[hsl(var(--terracotta))] mb-2">No pudimos encontrar el turno</div>
            <p className="text-sm text-neutral-600">
              El link ya no es válido o el turno fue cancelado. Por favor comunicate con el consultorio.
            </p>
          </div>
        )}

        {!loading && turno && (
          <div className="card-clinic p-8">
            {turno.estado === "cancelado" ? (
              <div className="text-center">
                <div className="overline text-[hsl(var(--terracotta))] mb-2">Turno cancelado</div>
                <p className="text-sm text-neutral-600">
                  Este turno fue cancelado. Comunicate con el consultorio para reprogramarlo.
                </p>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="overline mb-2">Tu turno en el consultorio</div>
                  <h1 className="text-2xl font-bold tracking-tight text-neutral-900">
                    Hola{turno.paciente_nombre ? `, ${turno.paciente_nombre.split(" ")[0]}` : ""}
                  </h1>
                  <p className="text-sm text-neutral-500 mt-1">
                    Estos son los detalles de tu próxima consulta:
                  </p>
                </div>

                <ul className="space-y-4 text-sm mb-8">
                  <Item icon={CalendarDays} label="Fecha" value={formatFecha(turno.fecha)} />
                  <Item icon={Clock} label="Hora" value={`${turno.hora} hs`} />
                  <Item icon={User} label="Profesional" value={`Lic. ${turno.profesional_nombre}`} />
                  <Item icon={CheckCircle2} label="Modalidad" value={TIPO_LABEL[turno.tipo] || turno.tipo} />
                </ul>

                {turno.confirmado_por_paciente ? (
                  <div className="rounded-xl border border-[hsl(var(--sage))]/40 bg-[hsl(var(--sage-light))] px-5 py-4 flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[hsl(var(--sage-hover))]" />
                    <div>
                      <div className="text-sm font-semibold text-[hsl(var(--sage-hover))]">
                        ¡Turno confirmado!
                      </div>
                      <div className="text-xs text-[hsl(var(--sage-hover))]/80">
                        Nos vemos pronto. Si necesitás reprogramar, escribinos por WhatsApp.
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    data-testid="confirmar-btn"
                    onClick={confirmar}
                    disabled={confirming}
                    className="btn-primary-pill w-full"
                  >
                    {confirming ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirmando…
                      </>
                    ) : (
                      "Confirmar mi asistencia"
                    )}
                  </button>
                )}

                <p className="text-xs text-neutral-500 text-center mt-6 leading-relaxed">
                  Si no podés asistir, por favor avisá con al menos 24 hs de anticipación.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Item({ icon: Icon, label, value }) {
  return (
    <li className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--sage-light))] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-[hsl(var(--sage-hover))]" />
      </div>
      <div className="min-w-0">
        <div className="overline text-neutral-500 mb-0.5">{label}</div>
        <div className="text-neutral-900 font-semibold">{value}</div>
      </div>
    </li>
  );
}
